/**
 * PlaylistEngine — Stateful, framework-agnostic timeline engine.
 *
 * Composes pure operations from ./operations with an event emitter
 * and optional PlayoutAdapter for audio playback delegation.
 */

import type { AudioClip, ClipTrack } from '@waveform-playlist/core';
import { sortClipsByTime } from '@waveform-playlist/core';
import {
  constrainClipDrag,
  constrainBoundaryTrim,
  canSplitAt,
  splitClip as splitClipOp,
} from './operations/clipOperations';
import { calculateDuration, findClosestZoomIndex } from './operations/timelineOperations';
import type { PlayoutAdapter, EngineState, EngineEvents, PlaylistEngineOptions } from './types';

const DEFAULT_SAMPLE_RATE = 48000;
const DEFAULT_SAMPLES_PER_PIXEL = 1024;
const DEFAULT_ZOOM_LEVELS = [256, 512, 1024, 2048, 4096, 8192];
const DEFAULT_MIN_DURATION_SECONDS = 0.1;

type EventName = keyof EngineEvents;

export class PlaylistEngine {
  private _tracks: ClipTrack[] = [];
  private _currentTime = 0;
  private _playStartPosition = 0;
  private _isPlaying = false;
  private _selectedTrackId: string | null = null;
  private _sampleRate: number;
  private _zoomLevels: number[];
  private _zoomIndex: number;
  private _selectionStart = 0;
  private _selectionEnd = 0;
  private _masterVolume = 1.0;
  private _loopStart = 0;
  private _loopEnd = 0;
  private _isLoopEnabled = false;
  private _bpm: number;
  private _ppqn: number;
  private _tracksVersion = 0;
  private _mixerVersion = 0;
  private _adapter: PlayoutAdapter | null;
  private _disposed = false;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  private _listeners: Map<string, Set<Function>> = new Map();

  private _undoStack: ClipTrack[][] = [];
  private _redoStack: ClipTrack[][] = [];
  private _inTransaction = false;
  private _transactionSnapshot: ClipTrack[] | null = null;
  private _transactionMutated = false;
  readonly undoLimit: number;

  constructor(options: PlaylistEngineOptions = {}) {
    this._sampleRate = options.sampleRate ?? DEFAULT_SAMPLE_RATE;
    this._zoomLevels = [...(options.zoomLevels ?? DEFAULT_ZOOM_LEVELS)];
    this._adapter = options.adapter ?? null;
    this.undoLimit = options.undoLimit ?? 100;

    // Bounded playback (#608): adapters that self-terminate at an endTime
    // report completion here. Deferred via queueMicrotask — the Tone adapter
    // fires this from inside the Transport tick chain, which catches nothing;
    // stopping the Transport re-entrantly from a tick callback is the hazard
    // the microtask sidesteps. Re-check _isPlaying inside: a consumer stop()
    // can land between the callback and the microtask.
    this._adapter?.onPlaybackEnded?.(() => {
      queueMicrotask(() => {
        if (this._isPlaying && !this._disposed) {
          this.stop();
        }
      });
    });

    if (this._zoomLevels.length === 0) {
      throw new Error('PlaylistEngine: zoomLevels must not be empty');
    }

    const initialSpp = options.samplesPerPixel ?? DEFAULT_SAMPLES_PER_PIXEL;
    const zoomIndex = this._zoomLevels.indexOf(initialSpp);
    if (zoomIndex === -1) {
      throw new Error(
        `PlaylistEngine: samplesPerPixel ${initialSpp} is not in zoomLevels [${this._zoomLevels.join(', ')}]. ` +
          `Either pass a samplesPerPixel value that exists in zoomLevels, or include ${initialSpp} in your zoomLevels array.`
      );
    }
    this._zoomIndex = zoomIndex;
    this._bpm = options.bpm ?? 120;
    // Adapter is the source of truth for PPQN. Headless mode falls back to options.ppqn or 960.
    this._ppqn = options.adapter?.ppqn ?? options.ppqn ?? 960;
  }

  // ---------------------------------------------------------------------------
  // Undo/Redo
  // ---------------------------------------------------------------------------

  get canUndo(): boolean {
    return this._undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this._redoStack.length > 0;
  }

  undo(): void {
    if (this._undoStack.length === 0) return;
    const snapshot = this._undoStack.pop()!;
    this._redoStack.push(this._snapshotTracks());
    this._restoreTracks(snapshot);
  }

  redo(): void {
    if (this._redoStack.length === 0) return;
    const snapshot = this._redoStack.pop()!;
    this._undoStack.push(this._snapshotTracks());
    this._restoreTracks(snapshot);
  }

  clearHistory(): void {
    this._undoStack = [];
    this._redoStack = [];
  }

  beginTransaction(): void {
    if (this._inTransaction) {
      console.warn(
        '[waveform-playlist/engine] beginTransaction: already in a transaction, ' +
          'previous snapshot will be overwritten'
      );
    }
    this._transactionSnapshot = this._snapshotTracks();
    this._inTransaction = true;
    this._transactionMutated = false;
  }

  commitTransaction(): void {
    if (!this._inTransaction || this._transactionSnapshot === null) {
      console.warn('[waveform-playlist/engine] commitTransaction: no active transaction to commit');
      return;
    }
    // Only push undo entry if mutations actually occurred during the transaction.
    // Without this, no-op drags (e.g., against a boundary) create phantom undo entries.
    if (this._transactionMutated) {
      this._undoStack.push(this._transactionSnapshot);
      if (this._undoStack.length > this.undoLimit) {
        this._undoStack.shift();
      }
      this._redoStack = [];
    }
    this._transactionSnapshot = null;
    this._inTransaction = false;
    this._transactionMutated = false;
  }

  abortTransaction(): void {
    if (!this._inTransaction || this._transactionSnapshot === null) {
      console.warn('[waveform-playlist/engine] abortTransaction: no active transaction to abort');
      return;
    }
    const snapshot = this._transactionSnapshot;
    const mutated = this._transactionMutated;
    this._transactionSnapshot = null;
    this._inTransaction = false;
    this._transactionMutated = false;
    // Only restore if mutations occurred — avoids full adapter rebuild on click
    if (mutated) {
      this._restoreTracks(snapshot);
    }
  }

  // ---------------------------------------------------------------------------
  // State snapshot
  // ---------------------------------------------------------------------------

  getState(): EngineState {
    return {
      tracks: this._tracks.map((t) => ({ ...t, clips: [...t.clips] })),
      tracksVersion: this._tracksVersion,
      mixerVersion: this._mixerVersion,
      duration: calculateDuration(this._tracks),
      currentTime: this._currentTime,
      isPlaying: this._isPlaying,
      samplesPerPixel: this._zoomLevels[this._zoomIndex],
      sampleRate: this._sampleRate,
      selectedTrackId: this._selectedTrackId,
      zoomIndex: this._zoomIndex,
      canZoomIn: this._zoomIndex > 0,
      canZoomOut: this._zoomIndex < this._zoomLevels.length - 1,
      selectionStart: this._selectionStart,
      selectionEnd: this._selectionEnd,
      masterVolume: this._masterVolume,
      loopStart: this._loopStart,
      loopEnd: this._loopEnd,
      isLoopEnabled: this._isLoopEnabled,
      bpm: this._bpm,
      ppqn: this._ppqn,
      canUndo: this.canUndo,
      canRedo: this.canRedo,
    };
  }

  // ---------------------------------------------------------------------------
  // Track Management
  // ---------------------------------------------------------------------------

  setTracks(tracks: ClipTrack[]): void {
    this.clearHistory();
    this._tracks = tracks.map((track) => ({
      ...track,
      clips: track.clips.map((clip) => ({
        ...clip,
        startTick: clip.startTick ?? this._secondsToTicks(clip.startSample / this._sampleRate),
      })),
    }));
    this._tracksVersion++;
    this._adapter?.setTracks(this._tracks);
    this._emitStateChange();
  }

  addTrack(track: ClipTrack): void {
    this._pushUndoSnapshot();
    const enriched = {
      ...track,
      clips: track.clips.map((clip) => ({
        ...clip,
        startTick: clip.startTick ?? this._secondsToTicks(clip.startSample / this._sampleRate),
      })),
    };
    this._tracks = [...this._tracks, enriched];
    this._tracksVersion++;
    if (this._adapter?.addTrack) {
      this._adapter.addTrack(enriched);
    } else {
      this._adapter?.setTracks(this._tracks);
    }
    this._emitStateChange();
  }

  removeTrack(trackId: string): void {
    if (!this._tracks.some((t) => t.id === trackId)) return;
    this._pushUndoSnapshot();
    this._tracks = this._tracks.filter((t) => t.id !== trackId);
    this._tracksVersion++;
    if (this._selectedTrackId === trackId) {
      this._selectedTrackId = null;
    }
    if (this._adapter?.removeTrack) {
      this._adapter.removeTrack(trackId);
    } else {
      this._adapter?.setTracks(this._tracks);
    }
    this._emitStateChange();
  }

  /**
   * Move a track to a new position in the track order. Purely organizational —
   * track order is not audible (adapter nodes are keyed by track id), so the
   * adapter is deliberately NOT called: a setTracks() here would rebuild
   * playout and interrupt playback for a visual-only change.
   */
  reorderTrack(trackId: string, toIndex: number): void {
    const fromIndex = this._tracks.findIndex((t) => t.id === trackId);
    if (fromIndex === -1) return;
    const clamped = Math.max(0, Math.min(toIndex, this._tracks.length - 1));
    if (clamped === fromIndex) return;
    this._pushUndoSnapshot();
    const track = this._tracks[fromIndex];
    const without = this._tracks.filter((t) => t.id !== trackId);
    this._tracks = [...without.slice(0, clamped), track, ...without.slice(clamped)];
    this._tracksVersion++;
    this._emitStateChange();
  }

  /** Update a single track's clips on the adapter (no full rebuild). */
  updateTrack(trackId: string, track?: ClipTrack): void {
    const resolved = track ?? this._tracks.find((t) => t.id === trackId);
    if (!resolved) return;
    if (track) {
      this._pushUndoSnapshot();
      const enriched = {
        ...track,
        clips: track.clips.map((clip) => ({
          ...clip,
          startTick: clip.startTick ?? this._secondsToTicks(clip.startSample / this._sampleRate),
        })),
      };
      this._tracks = this._tracks.map((t) => (t.id === trackId ? enriched : t));
      this._tracksVersion++;
      // Forward enriched track to adapter
      if (this._adapter?.updateTrack) {
        this._adapter.updateTrack(trackId, enriched);
      } else {
        this._adapter?.setTracks(this._tracks);
      }
      this._emitStateChange();
      return;
    }
    // No track arg — just forward existing track to adapter
    if (this._adapter?.updateTrack) {
      this._adapter.updateTrack(trackId, resolved);
    } else {
      this._adapter?.setTracks(this._tracks);
    }
  }

  /** Internal: update adapter after modifying this._tracks in place. */
  private _updateTrackOnAdapter(trackId: string): void {
    const t = this._tracks.find((tr) => tr.id === trackId);
    if (!t) return;
    if (this._adapter?.updateTrack) {
      this._adapter.updateTrack(trackId, t);
    } else {
      this._adapter?.setTracks(this._tracks);
    }
  }

  selectTrack(trackId: string | null): void {
    if (trackId === this._selectedTrackId) return;
    this._selectedTrackId = trackId;
    this._emitStateChange();
  }

  // ---------------------------------------------------------------------------
  // Clip Queries
  // ---------------------------------------------------------------------------

  /** Get a clip's full bounds for trim constraint computation. Returns null if not found. */
  getClipBounds(
    trackId: string,
    clipId: string
  ): {
    offsetSamples: number;
    durationSamples: number;
    startSample: number;
    sourceDurationSamples: number;
  } | null {
    const track = this._tracks.find((t) => t.id === trackId);
    if (!track) return null;
    const clip = track.clips.find((c: AudioClip) => c.id === clipId);
    if (!clip) return null;
    return {
      offsetSamples: clip.offsetSamples,
      durationSamples: clip.durationSamples,
      startSample: clip.startSample,
      sourceDurationSamples: clip.sourceDurationSamples,
    };
  }

  /** Constrain a trim delta using the engine's collision/bounds logic.
   *  Uses the clip's current state and neighboring clips for constraints. */
  constrainTrimDelta(
    trackId: string,
    clipId: string,
    boundary: 'left' | 'right',
    deltaSamples: number
  ): number {
    const track = this._tracks.find((t) => t.id === trackId);
    if (!track) return 0;
    const clipIndex = track.clips.findIndex((c: AudioClip) => c.id === clipId);
    if (clipIndex === -1) return 0;

    const clip = track.clips[clipIndex];
    const sortedClips = sortClipsByTime(track.clips);
    const sortedIndex = sortedClips.findIndex((c: AudioClip) => c.id === clipId);
    const minDuration = Math.floor(DEFAULT_MIN_DURATION_SECONDS * this._sampleRate);

    return constrainBoundaryTrim(
      clip,
      deltaSamples,
      boundary,
      sortedClips,
      sortedIndex,
      minDuration
    );
  }

  // ---------------------------------------------------------------------------
  // Clip Editing (delegates to operations/)
  // ---------------------------------------------------------------------------

  /** Move a clip by deltaSamples. Returns the constrained delta actually applied (0 if no-op). */
  moveClip(trackId: string, clipId: string, deltaSamples: number, skipAdapter = false): number {
    const track = this._tracks.find((t) => t.id === trackId);
    if (!track) {
      console.warn(`[waveform-playlist/engine] moveClip: track "${trackId}" not found`);
      return 0;
    }

    const clipIndex = track.clips.findIndex((c: AudioClip) => c.id === clipId);
    if (clipIndex === -1) {
      console.warn(
        `[waveform-playlist/engine] moveClip: clip "${clipId}" not found in track "${trackId}"`
      );
      return 0;
    }

    const clip = track.clips[clipIndex];
    const sortedClips = sortClipsByTime(track.clips);
    const sortedIndex = sortedClips.findIndex((c: AudioClip) => c.id === clipId);

    const constrainedDelta = constrainClipDrag(clip, deltaSamples, sortedClips, sortedIndex);

    if (constrainedDelta === 0) return 0;

    this._pushUndoSnapshot();

    this._tracks = this._tracks.map((t) => {
      if (t.id !== trackId) return t;
      const newClips = t.clips.map((c: AudioClip, i: number) => {
        if (i !== clipIndex) return c;
        const newStartSample = Math.floor(c.startSample + constrainedDelta);
        return {
          ...c,
          startSample: newStartSample,
          startTick:
            c.startTick !== undefined
              ? this._secondsToTicks(newStartSample / this._sampleRate)
              : undefined,
        };
      });
      return { ...t, clips: newClips };
    });

    this._tracksVersion++;
    if (!skipAdapter) {
      this._updateTrackOnAdapter(trackId);
    }
    this._emitStateChange();
    return constrainedDelta;
  }

  splitClip(trackId: string, clipId: string, atSample: number): void {
    const track = this._tracks.find((t) => t.id === trackId);
    if (!track) {
      console.warn(`[waveform-playlist/engine] splitClip: track "${trackId}" not found`);
      return;
    }

    const clipIndex = track.clips.findIndex((c: AudioClip) => c.id === clipId);
    if (clipIndex === -1) {
      console.warn(
        `[waveform-playlist/engine] splitClip: clip "${clipId}" not found in track "${trackId}"`
      );
      return;
    }

    const clip = track.clips[clipIndex];
    const minDuration = Math.floor(DEFAULT_MIN_DURATION_SECONDS * this._sampleRate);

    if (!canSplitAt(clip, atSample, minDuration)) {
      console.warn(
        `[waveform-playlist/engine] splitClip: cannot split clip "${clipId}" at sample ${atSample} ` +
          `(clip range: ${clip.startSample}–${clip.startSample + clip.durationSamples}, minDuration: ${minDuration})`
      );
      return;
    }

    this._pushUndoSnapshot();

    const { left, right } = splitClipOp(clip, atSample);

    // Enrich split clips with startTick if the original had one
    const enrichedLeft =
      clip.startTick !== undefined
        ? { ...left, startTick: this._secondsToTicks(left.startSample / this._sampleRate) }
        : left;
    const enrichedRight =
      clip.startTick !== undefined
        ? { ...right, startTick: this._secondsToTicks(right.startSample / this._sampleRate) }
        : right;

    this._tracks = this._tracks.map((t) => {
      if (t.id !== trackId) return t;
      const newClips = [...t.clips];
      newClips.splice(clipIndex, 1, enrichedLeft, enrichedRight);
      return { ...t, clips: newClips };
    });

    this._tracksVersion++;
    this._updateTrackOnAdapter(trackId);
    this._emitStateChange();
  }

  trimClip(
    trackId: string,
    clipId: string,
    boundary: 'left' | 'right',
    deltaSamples: number,
    skipAdapter = false
  ): void {
    const track = this._tracks.find((t) => t.id === trackId);
    if (!track) {
      console.warn(`[waveform-playlist/engine] trimClip: track "${trackId}" not found`);
      return;
    }

    const clipIndex = track.clips.findIndex((c: AudioClip) => c.id === clipId);
    if (clipIndex === -1) {
      console.warn(
        `[waveform-playlist/engine] trimClip: clip "${clipId}" not found in track "${trackId}"`
      );
      return;
    }

    const clip = track.clips[clipIndex];
    const sortedClips = sortClipsByTime(track.clips);
    const sortedIndex = sortedClips.findIndex((c: AudioClip) => c.id === clipId);
    const minDuration = Math.floor(DEFAULT_MIN_DURATION_SECONDS * this._sampleRate);

    const constrained = constrainBoundaryTrim(
      clip,
      deltaSamples,
      boundary,
      sortedClips,
      sortedIndex,
      minDuration
    );

    if (constrained === 0) return;

    this._pushUndoSnapshot();

    this._tracks = this._tracks.map((t) => {
      if (t.id !== trackId) return t;
      const newClips = t.clips.map((c: AudioClip, i: number) => {
        if (i !== clipIndex) return c;
        if (boundary === 'left') {
          const newStartSample = c.startSample + constrained;
          return {
            ...c,
            startSample: newStartSample,
            startTick:
              c.startTick !== undefined
                ? this._secondsToTicks(newStartSample / this._sampleRate)
                : undefined,
            offsetSamples: c.offsetSamples + constrained,
            durationSamples: c.durationSamples - constrained,
          };
        } else {
          return { ...c, durationSamples: c.durationSamples + constrained };
        }
      });
      return { ...t, clips: newClips };
    });

    this._tracksVersion++;
    if (!skipAdapter) {
      this._updateTrackOnAdapter(trackId);
    }
    this._emitStateChange();
  }

  // ---------------------------------------------------------------------------
  // Playback (delegates to adapter, no-ops without adapter)
  // ---------------------------------------------------------------------------

  async init(): Promise<void> {
    if (this._adapter) {
      await this._adapter.init();
    }
  }

  play(startTime?: number, endTime?: number): void {
    const prevCurrentTime = this._currentTime;
    const prevPlayStartPosition = this._playStartPosition;

    if (startTime !== undefined) {
      this._currentTime = Math.max(0, startTime);
    }

    // Remember where playback started (Audacity-style: stop returns here)
    this._playStartPosition = this._currentTime;

    if (this._adapter) {
      // Configure loop state BEFORE play(). The adapter caches loopStart/
      // loopEnd/enabled from setLoop(), then TonePlayout.play() applies
      // them to the Transport before transport.start() and advances
      // Clock._lastUpdate to skip stale ghost ticks.
      if (endTime !== undefined) {
        // Disable Transport loop for duration-limited playback (selection/annotation)
        this._adapter.setLoop(false, this._loopStart, this._loopEnd);
      } else if (this._isLoopEnabled) {
        // Activate Transport loop if starting before loopEnd. Starting at or
        // past loopEnd plays to the end without looping (click-past-loop behavior).
        const beforeLoopEnd = this._currentTime < this._loopEnd;
        this._adapter.setLoop(beforeLoopEnd, this._loopStart, this._loopEnd);
      }
      try {
        this._adapter.play(this._currentTime, endTime);
      } catch (err) {
        // Restore state so the engine isn't left with a moved playhead
        // but no audio. The throw propagates to the caller.
        this._currentTime = prevCurrentTime;
        this._playStartPosition = prevPlayStartPosition;
        throw err;
      }
    }

    this._isPlaying = true;
    this._emit('play');
    this._emitStateChange();
  }

  pause(): void {
    this._isPlaying = false;
    this._adapter?.pause();
    if (this._adapter) {
      this._currentTime = this._adapter.getCurrentTime();
    }
    this._emit('pause');
    this._emitStateChange();
  }

  stop(): void {
    this._isPlaying = false;
    this._currentTime = this._playStartPosition;
    this._adapter?.setLoop(false, this._loopStart, this._loopEnd);
    this._adapter?.stop();
    // Seek adapter to play-start position so getCurrentTime() returns it
    this._adapter?.seek(this._currentTime);
    this._emit('stop');
    this._emitStateChange();
  }

  seek(time: number): void {
    this._currentTime = Math.max(0, time);
    this._adapter?.seek(this._currentTime);
    this._emitStateChange();
  }

  setMasterVolume(volume: number): void {
    if (volume === this._masterVolume) return;
    this._masterVolume = volume;
    this._adapter?.setMasterVolume(volume);
    this._emitStateChange();
  }

  setTempo(bpm: number, atTick?: number): void {
    if (!Number.isFinite(bpm) || bpm <= 0) {
      console.warn('[waveform-playlist/engine] setTempo: bpm must be a positive finite number');
      return;
    }
    if (bpm === this._bpm && atTick === undefined) return;
    // Commit only when the adapter accepts (an explicit `false` means refused,
    // e.g. the transport's multi-entry tempo-map guard — #407). Void-returning
    // and absent adapters count as accepted.
    const accepted = this._adapter?.setTempo?.(bpm, atTick);
    if (accepted === false) return;
    this._bpm = bpm;
    this._recomputeStartSamples();
    this._emitStateChange();
  }

  getCurrentTime(): number {
    if (this._adapter) {
      return this._adapter.getCurrentTime();
    }
    return this._currentTime;
  }

  /**
   * Audio scheduling lookahead in seconds — `getCurrentTime()` is this far ahead of
   * what the listener actually hears. Tone.js Transport reports a position that's
   * `lookAhead` ahead of audible (default 0.1s); native AudioContext-based adapters
   * have no lookahead and return 0. Visual consumers should use `getAudibleTime()`,
   * which applies this compensation only while playing.
   */
  get lookAhead(): number {
    return this._adapter?.lookAhead ?? 0;
  }

  /**
   * Playback position aligned with what the listener actually hears, for
   * visual consumers (playhead, progress overlays, auto-scroll).
   *
   * While playing: `getCurrentTime() − outputLatency − lookAhead`, held at
   * the play-start position during the pre-roll window (audio at the start
   * position isn't audible until ~outputLatency + lookAhead after `play()`,
   * so the cursor waits there instead of jumping backward).
   *
   * While not playing: the raw resting position. A stationary cursor has no
   * audible counterpart — seek/stop/pause positions display exactly.
   *
   * Storage stays raw: never feed this value back into `play()`/`seek()`,
   * which compounds the subtraction on every cycle. Use `getCurrentTime()`
   * for storage and resume positions.
   */
  getAudibleTime(): number {
    if (!this._isPlaying) {
      return this._currentTime;
    }
    const ctx = this._adapter?.audioContext;
    const outputLatency =
      ctx && 'outputLatency' in ctx ? ((ctx as AudioContext).outputLatency ?? 0) : 0;
    const raw = this.getCurrentTime();
    let t = raw - outputLatency - this.lookAhead;
    if (raw >= this._playStartPosition && t < this._playStartPosition) {
      t = this._playStartPosition;
    }
    return Number.isFinite(t) ? Math.max(0, t) : 0;
  }

  // ---------------------------------------------------------------------------
  // Selection & Loop
  // ---------------------------------------------------------------------------

  setSelection(start: number, end: number): void {
    const s = Math.min(start, end);
    const e = Math.max(start, end);
    if (s === this._selectionStart && e === this._selectionEnd) return;
    this._selectionStart = s;
    this._selectionEnd = e;
    this._emitStateChange();
  }

  setLoopRegion(start: number, end: number): void {
    const s = Math.min(start, end);
    const e = Math.max(start, end);
    if (s === this._loopStart && e === this._loopEnd) return;
    this._loopStart = s;
    this._loopEnd = e;
    this._adapter?.setLoop(
      this._isLoopEnabled && this._isBeforeLoopEnd(),
      this._loopStart,
      this._loopEnd
    );
    this._emitStateChange();
  }

  setLoopEnabled(enabled: boolean): void {
    if (enabled === this._isLoopEnabled) return;
    this._isLoopEnabled = enabled;
    this._adapter?.setLoop(enabled && this._isBeforeLoopEnd(), this._loopStart, this._loopEnd);
    this._emitStateChange();
  }

  // ---------------------------------------------------------------------------
  // Per-Track Audio (forwards to adapter, persists into _tracks, emits)
  // ---------------------------------------------------------------------------
  //
  // Each setter forwards to the adapter, mirrors the change into the engine's
  // own _tracks (so a later adapter rebuild via setTracks keeps the value),
  // and bumps _mixerVersion + emits statechange. These are NOT structural
  // changes, so they deliberately do NOT bump _tracksVersion — that would make
  // tracksVersion-gated consumers re-run expensive structural work (audio-graph
  // rewire, peak regeneration) on every volume/pan drag frame. Consumers that
  // cache a track snapshot (e.g. @dawcore/components' <daw-editor>) watch
  // _mixerVersion to refresh that cache cheaply, so an unrelated setTracks
  // rebuild no longer reverts live mixer state (#501).

  setTrackVolume(trackId: string, volume: number): void {
    const track = this._tracks.find((t) => t.id === trackId);
    if (track) track.volume = volume;
    this._adapter?.setTrackVolume(trackId, volume);
    this._mixerVersion++;
    this._emitStateChange();
  }

  setTrackMute(trackId: string, muted: boolean): void {
    const track = this._tracks.find((t) => t.id === trackId);
    if (track) track.muted = muted;
    this._adapter?.setTrackMute(trackId, muted);
    this._mixerVersion++;
    this._emitStateChange();
  }

  setTrackSolo(trackId: string, soloed: boolean): void {
    const track = this._tracks.find((t) => t.id === trackId);
    if (track) track.soloed = soloed;
    this._adapter?.setTrackSolo(trackId, soloed);
    this._mixerVersion++;
    this._emitStateChange();
  }

  setTrackPan(trackId: string, pan: number): void {
    const track = this._tracks.find((t) => t.id === trackId);
    if (track) track.pan = pan;
    this._adapter?.setTrackPan(trackId, pan);
    this._mixerVersion++;
    this._emitStateChange();
  }

  // ---------------------------------------------------------------------------
  // Zoom
  // ---------------------------------------------------------------------------

  zoomIn(): void {
    if (this._zoomIndex > 0) {
      this._zoomIndex--;
      this._emitStateChange();
    }
  }

  zoomOut(): void {
    if (this._zoomIndex < this._zoomLevels.length - 1) {
      this._zoomIndex++;
      this._emitStateChange();
    }
  }

  setZoomLevel(samplesPerPixel: number): void {
    const newIndex = findClosestZoomIndex(samplesPerPixel, this._zoomLevels);
    if (newIndex === this._zoomIndex) return;
    this._zoomIndex = newIndex;
    this._emitStateChange();
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------

  on<K extends EventName>(event: K, listener: EngineEvents[K]): void {
    if (!this._listeners.has(event)) {
      this._listeners.set(event, new Set());
    }
    this._listeners.get(event)!.add(listener);
  }

  off<K extends EventName>(event: K, listener: EngineEvents[K]): void {
    this._listeners.get(event)?.delete(listener);
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this._isPlaying = false;
    this._adapter?.onPlaybackEnded?.(null);
    try {
      this._adapter?.dispose();
    } catch (err) {
      console.warn('[waveform-playlist/engine] Error disposing adapter:', err);
    }
    this._listeners.clear();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private _snapshotTracks(): ClipTrack[] {
    return this._tracks.map((t) => ({ ...t, clips: t.clips.map((c) => ({ ...c })) }));
  }

  private _pushUndoSnapshot(): void {
    if (this._inTransaction) {
      this._transactionMutated = true;
      return;
    }
    this._undoStack.push(this._snapshotTracks());
    if (this._undoStack.length > this.undoLimit) {
      this._undoStack.shift();
    }
    this._redoStack = [];
  }

  private _restoreTracks(snapshot: ClipTrack[]): void {
    const oldTracks = this._tracks;
    this._tracks = snapshot;
    this._tracksVersion++;
    // Use incremental adapter updates when track count is unchanged —
    // avoids full playout rebuild that interrupts playback during undo/redo.
    if (this._adapter && oldTracks.length === snapshot.length) {
      for (let i = 0; i < snapshot.length; i++) {
        if (oldTracks[i] !== snapshot[i]) {
          this._updateTrackOnAdapter(snapshot[i].id);
        }
      }
    } else {
      this._adapter?.setTracks(this._tracks);
    }
    this._emitStateChange();
  }

  private _emit(event: string, ...args: unknown[]): void {
    const listeners = this._listeners.get(event);
    if (listeners) {
      for (const listener of listeners) {
        try {
          listener(...args);
        } catch (error) {
          console.warn('[waveform-playlist/engine] Error in event listener:', error);
        }
      }
    }
  }

  /**
   * Returns whether the current playback position is before loopEnd.
   * Used by setLoopEnabled/setLoopRegion during playback — if past loopEnd,
   * Transport loop stays off so playback continues to the end.
   * Note: play() uses an inline check instead — _isPlaying is still false
   * when play() runs, and this method returns true unconditionally when
   * not playing.
   */
  private _isBeforeLoopEnd(): boolean {
    if (!this._isPlaying) return true;
    const t = this._adapter?.getCurrentTime() ?? this._currentTime;
    return t < this._loopEnd;
  }

  private _ticksToSeconds(tick: number): number {
    if (this._adapter?.ticksToSeconds) {
      return this._adapter.ticksToSeconds(tick);
    }
    return (tick * 60) / (this._ppqn * this._bpm);
  }

  private _secondsToTicks(seconds: number): number {
    if (this._adapter?.secondsToTicks) {
      return this._adapter.secondsToTicks(seconds);
    }
    return Math.round((seconds * this._ppqn * this._bpm) / 60);
  }

  private _recomputeStartSamples(): void {
    this._tracks = this._tracks.map((track) => ({
      ...track,
      clips: track.clips.map((clip) => {
        if (clip.startTick === undefined) return clip;
        return {
          ...clip,
          startSample: Math.round(this._ticksToSeconds(clip.startTick) * this._sampleRate),
        };
      }),
    }));
    this._tracksVersion++;
    // Use incremental updates to avoid full playout rebuild during playback
    for (const track of this._tracks) {
      this._updateTrackOnAdapter(track.id);
    }
  }

  private _emitStateChange(): void {
    this._emit('statechange', this.getState());
  }
}
